import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Box,
  Switch,
  FormControlLabel
} from '@mui/material';

/**
 * Reusable reminder form component for creating and editing reminders
 * 
 * @param {Object} props
 * @param {Object} props.reminder - Reminder data for editing (null for new reminder)
 * @param {function} props.onChange - Function called when form values change
 * @param {Object} props.errors - Validation errors object
 */
const ReminderForm = ({ reminder = null, onChange, errors = {} }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    scheduledDate: new Date(),
    recurrence: 'none',
    isActive: true,
    ...reminder
  });

  // Update form if reminder prop changes
  useEffect(() => {
    if (reminder) {
      setFormData({
        ...reminder,
        scheduledDate: reminder.scheduledDate ? 
          new Date(reminder.scheduledDate).toISOString().slice(0, 16) : 
          new Date().toISOString().slice(0, 16)
      });
    }
  }, [reminder]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    const newValue = name === 'isActive' ? checked : value;
    
    const updatedFormData = {
      ...formData,
      [name]: newValue
    };
    
    setFormData(updatedFormData);
    
    if (onChange) {
      onChange(updatedFormData);
    }
  };



  return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            name="title"
            label="Title"
            fullWidth
            required
            value={formData.title}
            onChange={handleChange}
            error={!!errors.title}
            helperText={errors.title}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            name="description"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description || ''}
            onChange={handleChange}
            error={!!errors.description}
            helperText={errors.description}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            name="scheduledDate"
            label="Date & Time"
            type="datetime-local"
            fullWidth
            required
            value={formData.scheduledDate}
            onChange={handleChange}
            error={!!errors.scheduledDate}
            helperText={errors.scheduledDate}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.recurrence}>
            <InputLabel id="recurrence-label">Recurrence</InputLabel>
            <Select
              labelId="recurrence-label"
              name="recurrence"
              value={formData.recurrence}
              label="Recurrence"
              onChange={handleChange}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
            {errors.recurrence && (
              <FormHelperText>{errors.recurrence}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange}
                  name="isActive"
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </Grid>
      </Grid>
  );
};

export default ReminderForm;
